'use client';

import { c as _c } from "react/compiler-runtime";
import { FactiiiRequirement } from '@shared/all/domains/factiiis';
import { type AddFactiiiAction, type AddFactiiiState, collectRequirements, MAX_FACTIII_NAME_LENGTH } from '@shared/utils/helpers/addFactiii/formState';
import { findSection, type SectionKey } from '@shared/utils/helpers/addFactiii/sections';
import type { FactiiiTemplate } from '@shared/utils/helpers/addFactiii/templates';
import { CheckCircle, Loader2, Settings2, X } from 'lucide-react';
import type { Dispatch } from 'react';
import { LUCIDE } from '../../../utils/lucideIcons';
import Input from '../../UI/Input';
import { NavRow, t, Tile } from './parts';
type Props = {
  state: AddFactiiiState;
  dispatch: Dispatch<AddFactiiiAction>;
  template: FactiiiTemplate;
  forcedRequirements: FactiiiRequirement[];
  /** Undefined while the shell has nothing to say yet (name too short). */
  nameAvailable: boolean | undefined;
  isCheckingName: boolean;
  onChangeTemplate: () => void;
  onOpenOptions: () => void;
  onOpenSection: (key: SectionKey) => void;
  /** False when the caller already fixed the shape (a board list): there is no
   *  template to change. */
  showTemplateBar: boolean;
};

/**
 * A new factiii asks for one thing: a name. Everything else is optional and
 * lives on the options screen, except the one or two settings the chosen
 * template is actually about, which are lifted onto the form so the template
 * you picked is visible in what you are looking at.
 */
const FactiiiForm = t0 => {
  const $ = _c(51);
  const {
    state,
    dispatch,
    template,
    forcedRequirements,
    nameAvailable,
    isCheckingName,
    onChangeTemplate,
    onOpenOptions,
    onOpenSection,
    showTemplateBar
  } = t0;
  const requirements = collectRequirements(state, forcedRequirements);
  const isRoot = requirements.includes(FactiiiRequirement.ROOT);
  const showNameStatus = state.name.length >= 3 && (isCheckingName || nameAvailable !== undefined);
  let t1;
  if ($[0] !== onChangeTemplate || $[1] !== showTemplateBar || $[2] !== template.blurb || $[3] !== template.icon || $[4] !== template.title) {
    t1 = showTemplateBar && <div className="flex items-center gap-2.5 rounded-lg border border-ds-border-standard px-2.5 py-2"><Tile icon={LUCIDE[template.icon]} /><div className="min-w-0 flex-1"><p className={`truncate ${t.label}`}>{template.title}</p><p className={`truncate ${t.meta}`}>{template.blurb}</p></div><button type="button" onClick={onChangeTemplate} className="shrink-0 text-[11px] font-emphasis text-primary hover:underline">Change</button></div>;
    $[0] = onChangeTemplate;
    $[1] = showTemplateBar;
    $[2] = template.blurb;
    $[3] = template.icon;
    $[4] = template.title;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== dispatch) {
    t2 = name => {
      if (name.length > MAX_FACTIII_NAME_LENGTH) {
        return;
      }
      dispatch({
        type: "patch",
        patch: {
          name
        }
      });
    };
    $[6] = dispatch;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== isCheckingName || $[9] !== nameAvailable || $[10] !== showNameStatus) {
    t3 = showNameStatus ? [isCheckingName ? <Loader2 key="checking" size={15} className="animate-spin text-textSecondary" /> : nameAvailable ? <CheckCircle key="available" size={15} className="text-primary" /> : <X key="taken" size={15} className="text-destructive" />] : undefined;
    $[8] = isCheckingName;
    $[9] = nameAvailable;
    $[10] = showNameStatus;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== state.name || $[13] !== t2 || $[14] !== t3 || $[15] !== template.namePlaceholder) {
    t4 = <Input type="text" label="Name" value={state.name} placeholder={template.namePlaceholder} inputClassName="text-sm" autofocus={true} onChange={t2} icons={t3} />;
    $[12] = state.name;
    $[13] = t2;
    $[14] = t3;
    $[15] = template.namePlaceholder;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== state.name.length) {
    t5 = state.name.length > MAX_FACTIII_NAME_LENGTH - 6 && <p className={`text-end ${t.meta}`}>{state.name.length}/{MAX_FACTIII_NAME_LENGTH}</p>;
    $[17] = state.name.length;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t4 || $[20] !== t5) {
    t6 = <div className="space-y-1">{t4}{t5}</div>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const t7 = isRoot ? "textarea" : "text";
  const t8 = template.descriptionLabel ?? (isRoot ? "The rule \u2014 when should AI apply this?" : "Description (optional)");
  let t9;
  if ($[22] !== dispatch) {
    t9 = description => dispatch({
      type: "patch",
      patch: {
        description
      }
    });
    $[22] = dispatch;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const t10 = template.descriptionPlaceholder ?? (isRoot ? "e.g. The document reports a lab result for a blood test" : "What is it for?");
  let t11;
  if ($[24] !== state.description || $[25] !== t10 || $[26] !== t7 || $[27] !== t8 || $[28] !== t9) {
    t11 = <Input type={t7} label={t8} value={state.description} inputClassName="text-sm" rows={4} onChange={t9} placeholder={t10} />;
    $[24] = state.description;
    $[25] = t10;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== template.highlight) {
    t12 = template.highlight ?? [];
    $[30] = template.highlight;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== onOpenSection || $[33] !== state || $[34] !== t12) {
    let t14;
    if ($[36] !== onOpenSection || $[37] !== state) {
      t14 = key => {
        const section = findSection(key);
        return <NavRow key={key} icon={LUCIDE[section.icon]} label={section.title} value={section.summary(state)} attention={section.attention?.(state)} onClick={() => onOpenSection(key)} />;
      };
      $[36] = onOpenSection;
      $[37] = state;
      $[38] = t14;
    } else {
      t14 = $[38];
    }
    t13 = t12.map(t14);
    $[32] = onOpenSection;
    $[33] = state;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = state.isPrivate ? "Private" : "Public";
  let t15;
  if ($[39] !== onOpenOptions || $[40] !== t14) {
    t15 = <NavRow icon={Settings2} label="Options" value={t14} onClick={onOpenOptions} />;
    $[39] = onOpenOptions;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t13 || $[43] !== t15) {
    t16 = <div className="space-y-1.5">{t13}{t15}</div>;
    $[42] = t13;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <p className={`text-center ${t.meta}`}>A factiii can't be edited later — only deleted and made again.</p>;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t1 || $[47] !== t11 || $[48] !== t16 || $[49] !== t6) {
    t18 = <div className="space-y-3">{t1}{t6}{t11}{t16}{t17}</div>;
    $[46] = t1;
    $[47] = t11;
    $[48] = t16;
    $[49] = t6;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  return t18;
};
export default FactiiiForm;